import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { of } from 'rxjs';
import { timeout, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { APP_VERSION_STORAGE_KEYS, LocalBuildVersionInfo, RemoteVersionConfig } from '../models/app-version.model';

const STORED_VERSION_KEY = APP_VERSION_STORAGE_KEYS.buildVersion;
const STORED_VERSION_TIME_KEY = APP_VERSION_STORAGE_KEYS.buildTime;

const REMOTE_CONFIG_TIMEOUT_MS = 4000;
const MANIFEST_TIMEOUT_MS = 4000;

/**
 * Reads/compares build versions and (optionally) a remote update policy.
 * Never throws — every network call degrades to a safe fallback so a
 * flaky/offline version check can never block app startup.
 */
@Injectable({
  providedIn: 'root'
})
export class AppVersionService {

  constructor(private http: HttpClient) {}

  private log(message: string, ...rest: any[]) {
    console.log(`[AppVersion] ${message}`, ...rest);
  }

  /** Last build version this webview successfully booted with, if any. */
  getStoredVersion(): string | null {
    try {
      return window.localStorage.getItem(STORED_VERSION_KEY);
    } catch (e) {
      return null;
    }
  }

  setStoredVersion(version: string): void {
    try {
      window.localStorage.setItem(STORED_VERSION_KEY, version);
      window.localStorage.setItem(STORED_VERSION_TIME_KEY, new Date().toISOString());
    } catch (e) {
      this.log('Unable to persist build version (localStorage unavailable)', e);
    }
  }

  /**
   * Fetches assets/version.json with a cache-busting query param so this
   * always reads what's actually deployed, never a cached copy of the
   * manifest itself.
   */
  async fetchDeployedVersion(): Promise<LocalBuildVersionInfo | null> {
    const url = `${environment.versionManifestUrl}?_=${Date.now()}`;
    try {
      const result = await this.http.get<LocalBuildVersionInfo>(url, { headers: { 'Cache-Control': 'no-cache' } }).pipe(
        timeout(MANIFEST_TIMEOUT_MS),
        catchError(err => {
          this.log('Failed to fetch version manifest', err && err.message || err);
          return of(null);
        })
      ).toPromise();
      if (!result || !result.buildVersion) {
        return null;
      }
      return result;
    } catch (err) {
      this.log('Unexpected error fetching version manifest', err);
      return null;
    }
  }

  /**
   * Optional server-driven update policy. Disabled unless
   * environment.remoteVersionConfigUrl is configured.
   */
  async fetchRemoteConfig(): Promise<RemoteVersionConfig | null> {
    const url = environment.remoteVersionConfigUrl;
    if (!url) {
      return null;
    }
    try {
      const result = await this.http.get<RemoteVersionConfig>(`${url}?_=${Date.now()}`).pipe(
        timeout(REMOTE_CONFIG_TIMEOUT_MS),
        catchError(err => {
          this.log('Failed to fetch remote version config', err && err.message || err);
          return of(null);
        })
      ).toPromise();
      return result || null;
    } catch (err) {
      this.log('Unexpected error fetching remote version config', err);
      return null;
    }
  }

  /**
   * Dotted-numeric version compare (handles "1.2.3", "1.2.3+abcdef1",
   * differing segment counts). Returns <0 if a<b, 0 if equal, >0 if a>b.
   * Non-numeric segments (e.g. a trailing git hash) are ignored.
   */
  compareVersions(a: string, b: string): number {
    const strip = (v: string) => (v || '').split('+')[0].split('-')[0];
    const segsA = strip(a).split('.').map(n => parseInt(n, 10));
    const segsB = strip(b).split('.').map(n => parseInt(n, 10));
    const len = Math.max(segsA.length, segsB.length);
    for (let i = 0; i < len; i++) {
      const na = isNaN(segsA[i]) ? 0 : segsA[i];
      const nb = isNaN(segsB[i]) ? 0 : segsB[i];
      if (na !== nb) {
        return na - nb;
      }
    }
    return 0;
  }

  isForceUpdateRequired(currentVersion: string, remoteConfig: RemoteVersionConfig | null): boolean {
    if (!remoteConfig || !remoteConfig.forceUpdate || !remoteConfig.minimumVersion) {
      return false;
    }
    return this.compareVersions(currentVersion, remoteConfig.minimumVersion) < 0;
  }
}
